import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { AppShell } from "@/components/app/AppShell";
import { Toaster } from "@/components/ui/sonner";
import { Loader2 } from "lucide-react";

import LoginPage from "@/pages/LoginPage";
import DashboardPage from "@/pages/DashboardPage";
import LieferungenPage from "@/pages/LieferungenPage";
import MilchprobenPage from "@/pages/MilchprobenPage";
import AbrechnungenPage from "@/pages/AbrechnungenPage";
import LieferantenPage from "@/pages/LieferantenPage";
import AdminImportPage from "@/pages/AdminImportPage";
import AdminReihungPage from "@/pages/AdminReihungPage";
import AdminAbrechnungslaufPage from "@/pages/AdminAbrechnungslaufPage";
import AdminLieferantenPage from "@/pages/AdminLieferantenPage";
import AdminMilchsortenPage from "@/pages/AdminMilchsortenPage";
import AdminGruppenPage from "@/pages/AdminGruppenPage";
import AdminPreismodellPage from "@/pages/AdminPreismodellPage";
import AdminBenutzerPage from "@/pages/AdminBenutzerPage";
import AdminFtpPage from "@/pages/AdminFtpPage";
import AdminApiKeysPage from "@/pages/AdminApiKeysPage";
import AdminQualitaetPage from "@/pages/AdminQualitaetPage";
import AdminTourenPage from "@/pages/AdminTourenPage";
import AdminEinstellungenPage from "@/pages/AdminEinstellungenPage";
import AdminBackupPage from "@/pages/AdminBackupPage";
import AdminAuditlogPage from "@/pages/AdminAuditlogPage";
import LieferantenKartePage from "@/pages/LieferantenKartePage";
import AdminDokumentePage from "@/pages/AdminDokumentePage";
import AdminSmtpPage from "@/pages/AdminSmtpPage";
import LieferantPortalPage from "@/pages/LieferantPortalPage";
import DokumentAnsichtPage from "@/pages/DokumentAnsichtPage";
import GalerieAnsichtPage from "@/pages/GalerieAnsichtPage";
import UmfrageAntwortPage from "@/pages/UmfrageAntwortPage";
import ImpressumPage from "@/pages/ImpressumPage";
import DatenschutzPage from "@/pages/DatenschutzPage";

const Laden = () => (
  <div className="flex min-h-screen items-center justify-center bg-background">
    <Loader2 className="h-7 w-7 animate-spin text-primary" />
  </div>
);

const Protected = ({ children, rollen }) => {
  const { user, loading } = useAuth();
  if (loading) return <Laden />;
  if (!user) return <Navigate to="/login" replace />;
  if (rollen && !rollen.includes(user.rolle)) return <Navigate to="/" replace />;
  return <AppShell>{children}</AppShell>;
};

// Rollen-Gruppen für Route-Guards
const ADMIN = ["admin", "sysadmin"];               // Milchgeld-Admin + System-Admin
const SYSADMIN = ["sysadmin"];                     // nur System-Admin
const PORTAL = ["lieferant", "admin", "sysadmin"]; // Lieferant + Admins
const STAFF = ["admin", "sysadmin", "berater"];    // Admins + Berater

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/impressum" element={<ImpressumPage />} />
          <Route path="/datenschutz" element={<DatenschutzPage />} />
          <Route path="/" element={<Protected><DashboardPage /></Protected>} />
          <Route path="/lieferungen" element={<Protected rollen={PORTAL}><LieferungenPage /></Protected>} />
          <Route path="/milchproben" element={<Protected rollen={PORTAL}><MilchprobenPage /></Protected>} />
          <Route path="/abrechnungen" element={<Protected rollen={PORTAL}><AbrechnungenPage /></Protected>} />
          <Route path="/lieferanten" element={<Protected rollen={STAFF}><LieferantenPage /></Protected>} />
          <Route path="/karte" element={<Protected rollen={STAFF}><LieferantenKartePage /></Protected>} />

          {/* Portal Inhalte (alle angemeldeten Benutzer) */}
          <Route path="/portal" element={<Protected><LieferantPortalPage /></Protected>} />
          <Route path="/portal/dokument/:id" element={<Protected><DokumentAnsichtPage /></Protected>} />
          <Route path="/portal/galerie/:id" element={<Protected><GalerieAnsichtPage /></Protected>} />
          <Route path="/portal/umfrage/:id" element={<Protected><UmfrageAntwortPage /></Protected>} />

          {/* Admin-Bereich (Milchgeld) – admin + sysadmin */}
          <Route path="/admin/import" element={<Protected rollen={ADMIN}><AdminImportPage /></Protected>} />
          <Route path="/admin/reihungsdateien" element={<Protected rollen={ADMIN}><AdminReihungPage /></Protected>} />
          <Route path="/admin/abrechnungslauf" element={<Protected rollen={ADMIN}><AdminAbrechnungslaufPage /></Protected>} />
          <Route path="/admin/qualitaet" element={<Protected rollen={ADMIN}><AdminQualitaetPage /></Protected>} />
          <Route path="/admin/touren" element={<Protected rollen={ADMIN}><AdminTourenPage /></Protected>} />
          <Route path="/admin/lieferanten" element={<Protected rollen={ADMIN}><AdminLieferantenPage /></Protected>} />
          <Route path="/admin/milchsorten" element={<Protected rollen={ADMIN}><AdminMilchsortenPage /></Protected>} />
          <Route path="/admin/gruppen" element={<Protected rollen={ADMIN}><AdminGruppenPage /></Protected>} />
          <Route path="/admin/preismodell" element={<Protected rollen={ADMIN}><AdminPreismodellPage /></Protected>} />
          <Route path="/admin/dokumente" element={<Protected rollen={ADMIN}><AdminDokumentePage /></Protected>} />

          {/* System-Bereich – nur sysadmin */}
          <Route path="/admin/einstellungen" element={<Protected rollen={SYSADMIN}><AdminEinstellungenPage /></Protected>} />
          <Route path="/admin/benutzer" element={<Protected rollen={SYSADMIN}><AdminBenutzerPage /></Protected>} />
          <Route path="/admin/ftp" element={<Protected rollen={SYSADMIN}><AdminFtpPage /></Protected>} />
          <Route path="/admin/api-keys" element={<Protected rollen={SYSADMIN}><AdminApiKeysPage /></Protected>} />
          <Route path="/admin/backup" element={<Protected rollen={SYSADMIN}><AdminBackupPage /></Protected>} />
          <Route path="/admin/smtp" element={<Protected rollen={SYSADMIN}><AdminSmtpPage /></Protected>} />
          <Route path="/admin/auditlog" element={<Protected rollen={SYSADMIN}><AdminAuditlogPage /></Protected>} />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </AuthProvider>
  );
}

export default App;
